import {Injectable, Injector, runInInjectionContext} from '@angular/core';
import {
  addDoc,
  collection,
  CollectionReference,
  collectionData,
  doc,
  docData,
  DocumentReference,
  Firestore,
  updateDoc,
  writeBatch
} from '@angular/fire/firestore';
import {List, ListDto} from './model/list';
import {map, tap} from 'rxjs/operators';
import {ListItem, ListItemDto} from './model/list-item';
import {ListMapperService} from './list-mapper.service';
import {categories, categoryMap, overig} from './model/item-category';
import {IngredientService} from './ingredient.service';
import {CategoryItems} from './model/category-items';
import {combineLatest, Observable, Subject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ListsService {

  id: string;
  listDoc: DocumentReference<ListDto>;
  listItemsCollections: CollectionReference<ListItemDto>;

  groupedItems: CategoryItems[] = [];
  private readonly emptyList = {
    items: []
  } as List;
  list = this.emptyList;

  constructor(private firestore: Firestore, private listMapper: ListMapperService,
              private ingredientService: IngredientService, private injector: Injector) {
  }

  private static compare(a: ListItem, b: ListItem): number {
    return (a.name || '').toLowerCase().localeCompare(b.name);
  }

  loadById(id: string): Observable<CategoryItems[]> {
    const result = new Subject<CategoryItems[]>();

    this.list = this.emptyList;
    this.groupedItems = [];
    const listPath = `lists-new/${id}`;
    const itemsPath = `${listPath}/items`;

    runInInjectionContext(this.injector, () => {
      this.listDoc = doc(this.firestore, listPath) as DocumentReference<ListDto>;
      this.listItemsCollections = collection(this.firestore, itemsPath) as CollectionReference<ListItemDto>;

      combineLatest([
        docData(this.listDoc),
        collectionData(this.listItemsCollections, {idField: 'id'}),
        this.ingredientService.ingredients$
      ]).pipe(
        map(([listDto, listItemDtos]) => this.listMapper.toList(listDto, listItemDtos)),
        tap((list) => this.matchCategories(list))
      ).subscribe(list => {
        this.parseList(list);
        result.next(this.groupedItems);
      });
    });

    return result.asObservable();
  }

  parseList(list: List): void {
    this.groupedItems = [];
    this.list = list;
    const groupedItems = this.list.items.reduce((result, obj) => {
      const items = (result[obj.category.name] || []) as ListItem[];
      items.push(obj);
      result[obj.category.name] = items;
      return result;
    }, {});

    for (const category of categories) {
      let items = groupedItems[category.name];
      if (items) {
        items = items.sort((a, b) => ListsService.compare(a, b));
        this.groupedItems.push({
          category,
          items,
        });
      }
    }
  }

  updateAmount(item: ListItem): void {
    if (item.collected < item.amount) {
      // Always mark all as completed
      item.collected = item.amount;
    } else if (item.collected === item.amount) {
      item.collected = 0;
    }

    runInInjectionContext(this.injector, () => {
      updateDoc(doc(this.listItemsCollections, item.id), {
        collected: item.collected
      }).then();
    });
  }

  addItem(listItem: ListItem): Promise<any> {
    this.list.items.push(listItem);
    const itemDto = this.listMapper.toListItemDto(listItem);

    return runInInjectionContext(this.injector, () => Promise.all([
      addDoc(this.listItemsCollections, itemDto),
      this.ingredientService.addNewIngredients([listItem])
    ]));
  }

  async addList(list: List): Promise<string> {
    const listDto = this.listMapper.toListDto(list);

    return runInInjectionContext(this.injector, async () => {
      const listId = await addDoc(collection(this.firestore, 'lists-new'), listDto);
      await this.addListItems(listId.id, list);
      await this.ingredientService.addNewIngredients(list.items);
      return listId.id;
    });
  }

  private addListItems(listId: string, list: List): Promise<void> {
    const batch = writeBatch(this.firestore);
    list.items
      .map(dto => this.listMapper.toListItemDto(dto))
      .forEach(item => {
        const itemReference = doc(collection(this.firestore, `lists-new/${listId}/items`));
        batch.set(itemReference, item);
      });

    return batch.commit();
  }

  private matchCategories(list: List): void {
    list.items.forEach(listItem => {
      const ingredient = this.ingredientService.find(listItem.shortName);
      listItem.category = ingredient ? categoryMap[ingredient.type] : overig;
    });
  }
}
